import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone } from 'lucide-react';
import Metadata from '../components/Metadata';
import PageHero from '../components/PageHero';
import SectionHeading from '../components/SectionHeading';
import ServiceCard from '../components/ServiceCard';
import ImageGallery from '../components/ImageGallery';
import ReviewsSlider from '../components/ReviewsSlider';
import ContactForm from '../components/ContactForm';
import useCmsCollection from '../hooks/useCmsCollection';
import { assets, company, pageMeta, serviceCards, uspItems } from '../data/siteData';
import { normalizeGalleryItems } from '../lib/gallery';
import { assetUrl } from '../lib/media';

export default function HomePage() {
  const [activeUsp, setActiveUsp] = useState(0);
  const projectsQuery = useCmsCollection('galleri', {
    endpoint: 'v2',
    limit: 12,
    offset: 0,
    sort: 'updatedAt',
    order: 'desc',
  });

  const reviewsQuery = useCmsCollection('anmeldelser', { endpoint: 'raw' });
  const projects = normalizeGalleryItems(projectsQuery.items);
  const activeUspItem = uspItems[activeUsp] ?? uspItems[0];

  return (
    <>
      <Metadata
        title={pageMeta.home.title}
        description={pageMeta.home.description}
        image={assetUrl(pageMeta.home.ogImage, 1200)}
        path="/"
      />

      <PageHero
        eyebrow="RS Byg · Lokalt tømrerarbejde i Thisted"
        title="Din lokale tømrer i Thisted og omegn"
        description="RS Byg udfører alt indenfor tømrerfaget med fokus på kvalitet, godt håndværk og holdbare løsninger. Vi hjælper blandt andet med træterrasser, renoveringer, tagudskiftning samt montering af nye vinduer og døre."
        image={assets.hero}
        primaryAction={{ label: 'Få et tilbud', to: '/kontakt' }}
        secondaryAction={{ label: 'Ring 42 30 79 39', href: company.phoneHref }}
        stats={[
          { label: 'Lokalt fokus', value: 'Vi arbejder i Thisted og omegn med løsninger, der passer til danske boliger og et dansk klima.' },
          { label: 'Kvalitet først', value: 'Fra første rådgivning til sidste skrue er kvalitet og præcision det naturlige udgangspunkt.' },
          { label: 'Direkte kontakt', value: 'Du taler med en håndværker, der følger opgaven hele vejen og holder dig opdateret.' },
        ]}
      />

      <section className="border-b border-brand-gray-200 bg-brand-gray-50 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Ydelser"
            title="Tømrerarbejde med et gennemført og professionelt udtryk"
            description="Vores arbejde spænder fra udendørs træterrasser til indvendige renoveringer, tage samt vinduer og døre. Hver løsning bliver tilpasset huset, detaljerne og de praktiske behov."
          />

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {serviceCards.map((service) => (
              <ServiceCard key={service.slug} service={service} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-brand-gray-200 bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Hvorfor vælge RS Byg"
            title="Et samarbejde med fokus på kvalitet, tydelig kommunikation og lokale løsninger"
            description="Vi arbejder struktureret og ordentligt, så du oplever et professionelt forløb fra start til slut."
          />

          <div className="mt-10 space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 lg:grid-cols-2">
              {uspItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = index === activeUsp;

                return (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => setActiveUsp(index)}
                    className={`group flex items-center gap-4 border p-4 text-left transition-colors ${isActive ? 'border-brand-black bg-brand-black text-white' : 'border-brand-gray-200 bg-brand-gray-50 text-brand-black hover:border-brand-orange hover:text-brand-orange'}`}
                    aria-pressed={isActive}
                  >
                    <span className={`flex h-12 w-12 shrink-0 items-center justify-center transition-colors ${isActive ? 'bg-brand-orange text-white' : 'bg-brand-black text-white group-hover:bg-brand-orange'}`}>
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className="font-display text-base font-bold leading-tight">{item.label}</span>
                  </button>
                );
              })}
            </div>

            <article className="flex h-full flex-col justify-between border border-brand-gray-200 bg-white p-6 sm:p-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-orange">Udvalgt USP</p>
                <h3 className="mt-4 font-display text-3xl font-bold tracking-tight text-brand-black sm:text-4xl">
                  {activeUspItem.label}
                </h3>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-brand-gray-600 sm:text-base">
                  {activeUspItem.text}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="border-b border-brand-gray-200 bg-brand-gray-50 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Udvalgte projekter"
            title="Se et udvalg af udførte projekter fra de relevante gallerier"
            description="Klik på et projekt for at åbne en stor visning. Galleriet er touch-venligt og fungerer også godt på mobil."
          />

          <div className="mt-10">
            {projectsQuery.loading ? (
              <div className="border border-brand-gray-200 bg-white p-6 text-sm text-brand-gray-600">
                Henter projekter...
              </div>
            ) : projects.length ? (
              <ImageGallery items={projects} columnsClass="xl:grid-cols-4" />
            ) : (
              <div className="border border-brand-gray-200 bg-white p-6 text-sm text-brand-gray-600">
                Der er endnu ingen projekter at vise.
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-brand-gray-200 bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Anmeldelser"
            title="Kundetilfredshed er en naturlig del af håndværket"
            description="Nedenfor ses et udvalg af realistiske kundereferencer fra afsluttede projekter."
          />

          <div className="mt-10">
            {reviewsQuery.loading ? (
              <div className="border border-brand-gray-200 bg-brand-gray-50 p-6 text-sm text-brand-gray-600">
                Henter anmeldelser...
              </div>
            ) : (
              <ReviewsSlider items={reviewsQuery.items} />
            )}
          </div>
        </div>
      </section>

      <section className="bg-brand-gray-50 py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
          <div className="space-y-6">
            <SectionHeading
              eyebrow="Kontakt"
              title="Kontakt RS Byg for et uforpligtende tilbud"
              description="Vi vender tilbage hurtigst muligt og hjælper dig gerne videre med både små og større projekter."
            />

            <div className="grid gap-4">
              <article className="border border-brand-gray-200 bg-white p-5">
                <div className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-[0.18em] text-brand-gray-500">Telefon</span>
                    <a href={company.phoneHref} className="text-sm font-bold text-brand-black whitespace-nowrap">
                      {company.phone}
                    </a>
                  </div>
                </div>
              </article>
              <article className="border border-brand-gray-200 bg-white p-5">
                <div className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-[0.18em] text-brand-gray-500">E-mail</span>
                    <a href={company.emailHref} className="text-sm font-bold text-brand-black whitespace-nowrap">
                      {company.email}
                    </a>
                  </div>
                </div>
              </article>
              <article className="border border-brand-gray-200 bg-white p-5">
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-orange" />
                  <div>
                    <span className="block text-xs font-bold uppercase tracking-[0.18em] text-brand-gray-500">Adresse</span>
                    <span className="text-sm font-bold text-brand-black">{company.address}</span>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <ContactForm />
        </div>
      </section>
    </>
  );
}
